import type {MigrateStylableComponentProps} from '../stylableMigration/migrateStylableComponent'
import {NEW_MENU} from './constants'

const CSS_PROP_TO_VAR_NAME = {
    label: {
        'text-decoration-line': 'text-decoration',
        'background-color': 'text-highlight'
    },
    scrollButtonIcon: {
        display: 'icon-display',
        width: 'icon-size',
        height: 'icon-size',
        fill: 'icon-color',
        transform: 'icon-rotation'
    },
    submenuListWrapper: {
        'column-count': 'columns-number'
    },
    submenuPositionBox: {
        'margin-left': 'horizontal-margin'
    }
} as const

type MigrationConfig = Omit<MigrateStylableComponentProps, 'serializedComponent'>

const selectorsMappings = {
    '.root': {
        name: 'container'
    },
    '.root::menu': {
        name: 'menu'
    },
    '.root::menuItem': {
        name: 'item'
    },
    '.root::menuItem::container': {
        name: 'item'
    },
    '.root::menuItem:isCurrentPage': {
        name: 'item-selected',
        root: 'item'
    },
    '.root::menuItem:isHovered': {
        name: 'item-hover',
        root: 'item'
    },
    '.root::menuItem:hover': {
        name: 'item-hover',
        root: 'item'
    },
    '.root::menuItem::label': {
        name: 'item',
        properties: CSS_PROP_TO_VAR_NAME.label
    },
    '.root::menuItem:isCurrentPage::label': {
        name: 'item-selected',
        root: 'item',
        properties: CSS_PROP_TO_VAR_NAME.label
    },
    '.root::menuItem:isHovered::label': {
        name: 'item-hover',
        root: 'item',
        properties: CSS_PROP_TO_VAR_NAME.label
    },
    '.root::menuItem:hover::label': {
        name: 'item-hover',
        root: 'item',
        properties: CSS_PROP_TO_VAR_NAME.label
    },
    '.root::scrollButton': {
        name: 'scroll-button'
    },
    '.root::scrollButton:hover': {
        name: 'scroll-button-hover',
        root: 'scroll-button'
    },
    '.root::scrollButton::icon': {
        name: 'scroll-button',
        properties: CSS_PROP_TO_VAR_NAME.scrollButtonIcon
    },
    '.root::scrollButton:hover::icon': {
        name: 'scroll-button-hover',
        root: 'scroll-button',
        properties: CSS_PROP_TO_VAR_NAME.scrollButtonIcon
    },
    '.root::columnsLayout': {
        name: 'dropdown-container'
    },
    '.root::columnsLayout::menuItem': {
        name: ['dropdown-menu-item', 'dropdown-menu-sub-item']
    },
    '.root::columnsLayout::menuItem:isHovered': {
        name: ['dropdown-menu-item-hover', 'dropdown-menu-sub-item-hover'],
        root: 'dropdown-menu-item'
    },
    '.root::columnsLayout::menuItem:isCurrentPage': {
        name: ['dropdown-menu-item-selected', 'dropdown-menu-sub-item-selected'],
        root: 'dropdown-menu-item'
    },
    '.root::columnsLayout::menuItem::label': {
        name: ['dropdown-menu-item', 'dropdown-menu-sub-item'],
        properties: CSS_PROP_TO_VAR_NAME.label
    },
    '.root::columnsLayout::menuItem:isHovered::label': {
        name: ['dropdown-menu-item-hover', 'dropdown-menu-sub-item-hover'],
        root: 'dropdown-menu-item',
        properties: CSS_PROP_TO_VAR_NAME.label
    },
    '.root::columnsLayout::menuItem:isCurrentPage::label': {
        name: ['dropdown-menu-item-selected', 'dropdown-menu-sub-item-selected'],
        root: 'dropdown-menu-item',
        properties: CSS_PROP_TO_VAR_NAME.label
    },
    '.root::columnsLayout::menuItem::container': {
        name: 'dropdown-menu'
    },
    '.root::columnsLayout::listWrapper': {
        name: 'dropdown-menu',
        properties: CSS_PROP_TO_VAR_NAME.submenuListWrapper
    },
    '.root::positionBox': {
        name: 'dropdown',
        properties: CSS_PROP_TO_VAR_NAME.submenuPositionBox
    },
    '.root::megaMenuWrapper': {
        name: 'mega-menu-wrapper'
    },
    '.root::containerPositionBox': {
        name: 'mega-menu-container-position-box'
    }
} as const

const setIfMissing = (
    properties: Record<string, string>,
    key: string,
    value: string,
    propertiesSource: Record<string, string>
) => {
    if (!properties[key]) {
        properties[key] = value
        propertiesSource[key] = 'value'
    }
}

const copyIfMissing = (
    properties: Record<string, string>,
    key: string,
    keyToCopy: string,
    propertiesSource: Record<string, string>
) => {
    if (!properties[key] && properties[keyToCopy]) {
        properties[key] = properties[keyToCopy]
        propertiesSource[key] = 'value'
    }
}

const defaultValues = [
    ['dropdown-container-background', 'rgba(var(--color_11),1)'],
    ['dropdown-menu-container-background', 'rgba(var(--color_11),0)'],
    ['dropdown-menu-item-background', 'rgba(var(--color_11),0)'],
    ['dropdown-menu-sub-item-background', 'rgba(var(--color_11),0)'],
    ['dropdown-menu-sub-items-vertical-spacing-before', '2px'],
    ['dropdown-menu-sub-item-vertical-padding', '2px'],
    ['dropdown-menu-container-horizontal-padding', '4px'],
    ['dropdown-space-above', '10px']
]

const copyValues = [
    ['dropdown-menu-item-font', 'item-font'],
    ['dropdown-menu-sub-item-font', 'item-font']
]

export const stylableMigrationConfig: MigrationConfig = {
    newSkin: NEW_MENU.skin,
    useCssColor: true,
    withBorderRadiusAsOneParam: true,
    postStyleMigrationCallback: style => {
        const {properties, propertiesSource} = style
        if (properties['scroll-button-icon-display'] === 'initial') {
            properties['scroll-button-icon-display'] = 'unset'
        }

        defaultValues.forEach(([key, value]) => setIfMissing(properties, key, value, propertiesSource))
        copyValues.forEach(([key, keyToCopy]) => copyIfMissing(properties, key, keyToCopy, propertiesSource))
        return style
    },
    selectorsMappings
}
